import { useEffect, useMemo } from 'react';

import { useFetchData, useInfiniteQueryData } from '@/api/apiHooks';
import { RECRUITMENT_MARKETING_PROVIDER_DETAILS } from '@/components/MarketingCampaign/components';
import {
  DropDownResponse,
  RecruitmentMarketingProvider,
  TrackingTemplatesResponse,
} from '@/utils/types';
import { URLS } from '@/utils/urls';

// cmdk (which powers SelectMenu) falls back to an item's label text as its
// selection value whenever `value` is falsy, so the "all providers" option
// can't use '' - it would never match a campaign and would hide every row.
export const ALL_PROVIDERS_VALUE = 'all';

export interface TrackingProviderOption {
  label: string;
  value: string;
}

/**
 * Loads the options for the marketing campaigns tracking-provider filter:
 * the panel's tracking templates plus the available tracking template
 * integration types, merged with a couple of hardcoded starter options.
 */
export const useTrackingProviderOptions = () => {
  const {
    isLoading: trackingTemplatesLoading,
    isFetching: trackingTemplatesFetching,
    data: trackingTemplatesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQueryData<TrackingTemplatesResponse>({
    url: URLS.getTrackingTemplates(),
  });
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const trackingTemplates = useMemo(
    () => trackingTemplatesData?.pages?.flatMap((page) => page.data) || [],
    [trackingTemplatesData]
  );

  const {
    isLoading: trackingTemplateIntegrationTypesLoading,
    isFetching: trackingTemplateIntegrationTypesFetching,
    data: trackingTemplateIntegrationTypesData,
  } = useFetchData<DropDownResponse>({
    url: URLS.getMarketingCampaignProviderTypes(),
  });

  const options = useMemo<TrackingProviderOption[]>(() => {
    const starters = [
      {
        label: 'All providers',
        value: ALL_PROVIDERS_VALUE,
      },
    ];

    const combined = starters
      .concat(
        trackingTemplates.map((template) => ({
          label: template.name,
          value: template.id,
        }))
      )
      .concat(
        trackingTemplateIntegrationTypesData?.data.map((integrationType) => {
          // This endpoint spells the platforms "Meta" / "Google", so the rest
          // of the UI's labels win here.
          const providerDetails =
            RECRUITMENT_MARKETING_PROVIDER_DETAILS[
              integrationType.key as RecruitmentMarketingProvider
            ];

          if (providerDetails) {
            return {
              label: providerDetails.label,
              value: providerDetails.provider,
            };
          }

          return {
            label: integrationType.value,
            value: integrationType.key,
          };
        }) ?? []
      );

    return combined.sort((a, b) => a.label.localeCompare(b.label));
  }, [trackingTemplates, trackingTemplateIntegrationTypesData]);

  return {
    options,
    isLoading:
      trackingTemplatesLoading || trackingTemplateIntegrationTypesLoading,
    isFetching:
      trackingTemplatesFetching || trackingTemplateIntegrationTypesFetching,
  };
};
